'use client';

import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useXR, useXRInputSourceState } from '@react-three/xr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group } from 'three';
import { spaceCanvasXrBindings, useXrButtonBinding, useXrIndicator, useXrKeyBinding, type XrIndicator }
    from '@toolproof-core/visualization/rendering/xr-bindings';
import { AGENT_CHAT_STATUS, MAX_AGENT_INSTRUCTION_LENGTH } from '@/_data/agentChat';
import { chatTextPages, editChatDraft, agentSceneCaption, adjustXrHudDistance } from '@/_pure/agentChatPresentation';
import { chatLocked, chatWorking, type AgentChatState } from './agentChatController';
import { useAgentChat, useVoiceInput, useSpeechPlayback } from './AgentChatProvider';
import { useMicrophoneTest } from './MicrophoneTestProvider';
import { createPushToTalk } from './pushToTalk';
import { voiceInputSummary } from './voiceInputController';
import { changeFileText, changesetSummary, selectedAgentChanges } from '@/_pure/agentChanges';
import { plannedFilesSummary, selectedPlannedFiles } from '@/_pure/agentPlannedFiles';
import { voiceDiagnostic } from './voiceDiagnostics';
import { ahpDiagnosticsText, useAhpStreamDiagnostics } from './AhpDiagnostics';
import { useTranscriptionTest } from './AgentChatProvider';

const OWNER = 'Agent';
let hudDistance = 2.8;

function agentIndicator(args: {
    authenticated: boolean; chat: AgentChatState;
    voice: ReturnType<typeof useVoiceInput>['state']; speaking: boolean;
}): XrIndicator {
    const { authenticated, chat, voice, speaking } = args;
    if (!authenticated) return { color: '#707880', label: 'Sign in required' };
    if (voice.phase === 'requesting') return { color: '#f0b030', label: 'Opening mic - keep X held' };
    if (voice.phase === 'recording') return { color: '#e04040', label: `Listening ${Math.floor(voice.seconds)}s` };
    if (voice.phase === 'stopping' || voice.phase === 'ready' || voice.phase === 'transcribing') return { color: '#40a0f0', label: 'Transcribing' };
    if (voice.phase === 'error' || chat.error || chat.view?.error) return { color: '#ff5050', label: 'Error - press Y' };
    if (chat.view?.plans?.some(plan => plan.pending)) return { color: '#f0b030', label: 'Plan ready - press Y' };
    if ((chat.view?.approvals?.length ?? 0) + (chat.view?.inputs?.length ?? 0) > 0) return { color: '#f0b030', label: 'Needs your answer' };
    if (chat.sending || chatWorking(chat)) return { color: '#8060f0', label: 'Agent working' };
    if (speaking) return { color: '#40d0d0', label: 'Speaking' };
    return { color: '#3ccf6e', label: 'Agent ready' };
}

function Label({ text, x = 0, y, size = 0.045, width = 2.65 }: {
    text: string; x?: number; y: number; size?: number; width?: number;
}) {
    return <Text position={[x, y, 0.015]} fontSize={size} maxWidth={width} lineHeight={1.35}
        anchorX="center" anchorY="middle" textAlign="left" color="white" raycast={() => null} renderOrder={11002}>
        {text}
        <meshBasicMaterial depthTest={false} depthWrite={false} />
    </Text>;
}

function Button({ text, x, y, width = 0.4, disabled = false, onClick }: {
    text: string; x: number; y: number; width?: number; disabled?: boolean; onClick: () => void;
}) {
    return <group position={[x, y, 0.025]}>
        <mesh renderOrder={11001} onClick={event => { event.stopPropagation(); if (!disabled) onClick(); }}
            onPointerDown={event => event.stopPropagation()} onPointerUp={event => event.stopPropagation()}>
            <planeGeometry args={[width, 0.13]} />
            <meshBasicMaterial color={disabled ? '#28313a' : '#24506c'} depthTest={false} depthWrite={false} />
        </mesh>
        <Label text={text} y={0} size={0.038} width={width - 0.02} />
    </group>;
}

export function XrAgentChat() {
    const { authenticated } = useAgentChat();
    const session = useXR(value => value.session);
    useEffect(() => {
        if (!session) return;
        voiceDiagnostic('xr-start');
        const ended = () => voiceDiagnostic('xr-end');
        const visibility = () => voiceDiagnostic('xr-visibility', { phase: session.visibilityState });
        session.addEventListener('end', ended);
        session.addEventListener('visibilitychange', visibility);
        return () => {
            voiceDiagnostic('xr-unmount');
            session.removeEventListener('end', ended);
            session.removeEventListener('visibilitychange', visibility);
        };
    }, [session]);
    return session ? <XrAgentChatPanel key={String(authenticated)} /> : null;
}

function XrAgentChatPanel() {
    const { state, controller, authenticated } = useAgentChat();
    const microphone = useMicrophoneTest();
    const voice = useVoiceInput();
    const test = useTranscriptionTest();
    const session = useXR(value => value.session);
    const rightController = useXRInputSourceState('controller', 'right');
    const [distance, setDistance] = useState(hudDistance);
    const [open, setOpen] = useState(false);
    const [composing, setComposing] = useState(false);
    const [ahpControls, setAhpControls] = useState(false);
    const [voicing, setVoicing] = useState(false);
    const [reading, setReading] = useState(false);
    const [voiceTools, setVoiceTools] = useState(false);
    const adjusting = useCallback(() => !!session && open
        && (rightController?.inputSource.gamepad?.buttons[1]?.value
            ?? rightController?.gamepad['xr-standard-squeeze']?.button ?? 0) > 0.5, [session, open, rightController]);
    useEffect(() => spaceCanvasXrBindings.captureStick('right', OWNER, adjusting), [adjusting]);
    const group = useRef<Group>(null);
    const locked = chatLocked(state);
    const history = state.view?.entries.map(entry => `${entry.role === 'user' ? 'You' : 'Copilot'}:\n${entry.content}`).join('\n\n') || 'No messages yet.';
    const pages = chatTextPages(history);
    const draftPages = chatTextPages(state.draft || 'Select Compose to type a request.', 52, 3);
    const error = [state.view?.error, state.error, !voice.state.enabled ? voice.state.error : undefined].filter(Boolean).join('\n');
    const errorPages = chatTextPages(error, 52, 3);
    const [historyPosition, setHistoryPosition] = useState({ text: '', index: 0 });
    const [draftPosition, setDraftPosition] = useState({ text: '', index: 0 });
    const [errorPosition, setErrorPosition] = useState({ text: '', index: 0 });
    const page = historyPosition.text === history ? Math.min(historyPosition.index, pages.length - 1) : pages.length - 1;
    const draftPage = draftPosition.text === state.draft ? Math.min(draftPosition.index, draftPages.length - 1) : draftPages.length - 1;
    const errorPage = errorPosition.text === error ? Math.min(errorPosition.index, errorPages.length - 1) : 0;
    const setPage = (index: number) => setHistoryPosition({ text: history, index });
    const setDraftPage = (index: number) => setDraftPosition({ text: state.draft, index });
    const setErrorPage = (index: number) => setErrorPosition({ text: error, index });
    const speech = useSpeechPlayback();
    const pushToTalk = useRef<ReturnType<typeof createPushToTalk> | null>(null);
    useEffect(() => {
        const created = createPushToTalk(voice.controller, controller, message => spaceCanvasXrBindings.reject('left', message));
        pushToTalk.current = created;
        return () => {
            created.dispose();
            if (pushToTalk.current === created) pushToTalk.current = null;
        };
    }, [voice.controller, controller]);
    const close = () => {
        // Hiding the panel must not discard a push-to-talk message that is still being transcribed or sent.
        if (!pushToTalk.current?.busy()) voice.controller.cancel();
        setOpen(false); setComposing(false); setVoicing(false); setReading(false);
    };
    useXrButtonBinding('y', { owner: OWNER, legend: 'show / hide Agent', onTap: () => {
        if (open) close();
        else setOpen(true);
    } });
    useXrKeyBinding('Tab', { owner: OWNER, key: 'Tab', legend: 'show / hide Agent', onKey: () => {
        if (open) close();
        else setOpen(true);
    } });
    useXrKeyBinding('Enter', authenticated ? { owner: OWNER, key: 'Enter', legend: 'type a request (Ctrl+Enter sends, Esc stops)',
        onKey: () => {
            if (locked) { spaceCanvasXrBindings.notify('Agent is busy - typing is locked'); return; }
            setOpen(true); setVoicing(false); setReading(false); setAhpControls(false); setComposing(true);
        } } : undefined);
    useXrKeyBinding('PageUp', open && !composing ? { owner: OWNER, key: 'PgUp', legend: 'previous messages',
        onKey: () => setPage(Math.max(0, page - 1)) } : undefined);
    useXrKeyBinding('PageDown', open && !composing ? { owner: OWNER, key: 'PgDn', legend: 'next messages',
        onKey: () => setPage(Math.min(pages.length - 1, page + 1)) } : undefined);
    useXrButtonBinding('x', authenticated ? {
        owner: OWNER, legend: test.state.selected ? 'hold for test; release finalizes (no send)' : 'hold to talk; release sends',
        triggerLegend: test.state.selected ? 'restart test' : 'cancel',
        onPress: () => {
            microphone.controller.dispose();
            if (test.state.selected) { speech.controller.stop(); void test.controller.record(); return; }
            const problem = pushToTalk.current?.press();
            if (problem) spaceCanvasXrBindings.reject('left', problem);
        },
        onRelease: held => { if (test.state.selected) void test.controller.finish(); else pushToTalk.current?.release(held); },
        onTrigger: () => {
            if (test.state.selected) { test.controller.restart(); spaceCanvasXrBindings.notify('Starting over — no-send test'); return; }
            if (!pushToTalk.current?.busy()) return;
            pushToTalk.current.cancel();
            spaceCanvasXrBindings.notify('Voice message cancelled');
        },
        onCancel: () => { if (test.state.selected) void test.controller.cancel(); else pushToTalk.current?.cancel(); },
    } : { owner: OWNER, legend: 'hold to talk (sign in first)',
        onTap: () => spaceCanvasXrBindings.reject('left', 'Sign in required - press Y') });
    useXrIndicator('left', OWNER, agentIndicator({
        authenticated, chat: state, voice: test.state.selected && test.state.phase === 'recording'
            ? { ...voice.state, phase: 'recording' } : test.state.selected && test.state.phase === 'requesting'
                ? { ...voice.state, phase: 'requesting' } : voice.state, speaking: speech.state.phase === 'speaking',
    }));
    useEffect(() => {
        if (!session || !open || !composing) return;
        const keydown = (event: KeyboardEvent) => {
            if (event.isComposing) return;
            event.stopImmediatePropagation();
            if (event.key === 'Escape') { event.preventDefault(); setComposing(false); return; }
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                if (locked || !controller.getSnapshot().draft.trim()) return;
                setComposing(false);
                void controller.send();
                return;
            }
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            event.preventDefault();
            if (locked) return;
            if (event.key === 'Enter' || event.key === 'Backspace' || Array.from(event.key).length === 1) {
                controller.setDraft(editChatDraft(controller.getSnapshot().draft, event.key));
            }
        };
        const paste = (event: ClipboardEvent) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (!locked && event.clipboardData) {
                controller.setDraft(controller.getSnapshot().draft + event.clipboardData.getData('text/plain'));
            }
        };
        window.addEventListener('keydown', keydown, true);
        window.addEventListener('paste', paste, true);
        return () => {
            window.removeEventListener('keydown', keydown, true);
            window.removeEventListener('paste', paste, true);
        };
    }, [session, open, composing, locked, controller]);
    useFrame(({ camera }, delta) => {
        if (adjusting()) {
            const axis = rightController?.gamepad['xr-standard-thumbstick']?.yAxis ?? 0;
            const next = adjustXrHudDistance(hudDistance, axis, delta);
            if (next !== hudDistance) {
                hudDistance = next;
                setDistance(hudDistance);
            }
        }
        if (!group.current) return;
        camera.getWorldPosition(group.current.position);
        camera.getWorldQuaternion(group.current.quaternion);
    });
    const status = state.loading ? 'Restoring chat...' : state.sending ? 'Sending...' : state.view
        ? AGENT_CHAT_STATUS[state.view.status] : 'Ready - local changes only; nothing is pushed.';
    const caption = authenticated && !open ? test.state.selected ? {
        text: `VOICE TEST — NO SEND\n${test.state.caption || 'Hold X and speak. Left trigger restarts.'}`,
        status: `${test.state.phase} | ${test.state.seconds.toFixed(1)}s${test.state.captureEnabled ? ' | SAVING (24h)' : ''}${test.state.comparing ? ' | Whisper pending' : test.state.whisper ? ' | Whisper ready' : ''}${test.state.error ? ` | ${test.state.error}` : ''}`,
        streaming: true,
    } : agentSceneCaption(state) : undefined;
    return <group ref={group} renderOrder={11000} pointerEventsOrder={11000}>
        {caption ? <XrAgentCaption key={`${state.threadId}:${caption.streaming ? 'streaming' : caption.text}`} {...caption} distance={distance} /> : null}
        {!open ? null : <group position={[0, 0, -distance]} scale={distance / 2.2}>
            <mesh renderOrder={11000} onClick={event => event.stopPropagation()}
                onPointerDown={event => event.stopPropagation()} onPointerUp={event => event.stopPropagation()}
                onPointerMove={event => event.stopPropagation()}>
                <planeGeometry args={[2.9, 2.75]} />
                <meshBasicMaterial color="#102030" depthTest={false} depthWrite={false} />
            </mesh>
            <Button text="Close" x={1.1} y={1.15} width={0.5} onClick={close} />
            <Label text={`Right grip + stick: distance ${distance.toFixed(1)}m | H: keyboard shortcuts`} y={1.3} size={0.025} />
            {!authenticated ? <>
                <Label text="Agent - sign in required" y={0.9} />
                <Label text={'You are not signed in on this browser.\n\nExit XR, open /auth in this browser and sign in with an authorized account, then enter XR again.'}
                    y={0.35} size={0.04} />
            </> : reading ? <>
                <Button text="Back to chat" x={-1} y={1.15} width={0.75} onClick={() => setReading(false)} />
                <XrSpeechPlayback />
            </> : voicing ? <>
                <Button text="Back to chat" x={-1} y={1.15} width={0.75} onClick={() => {
                    voice.controller.cancel(); setVoicing(false);
                }} />
                <XrVoiceInput />
            </> : ahpControls ? <>
                <Button text="Back to chat" x={-1} y={1.15} width={0.75} onClick={() => setAhpControls(false)} />
                <XrAhpControls />
            </> : <>
            <Button text="New chat" x={-1} y={1.15} width={0.65}
                disabled={!!state.voiceBusy || state.loading || state.sending || chatWorking(state)} onClick={controller.newChat} />
            {state.transport === 'ahp' ? <Button text={`Sessions / requests (${(state.view?.approvals?.length ?? 0) + (state.view?.inputs?.length ?? 0) + (state.view?.plans?.filter(plan => plan.pending).length ?? 0)})`}
                x={0} y={1.15} width={1.2} onClick={() => { setComposing(false); setAhpControls(true); }} /> : null}
            <Label text={state.development ? `${status} | LIVE ${state.development.id}` : status} y={0.98} size={0.038} />
            <Label text={state.view?.branch ? `Branch: ${state.view.branch}` : state.transport === 'ahp'
                ? `Mode: ${state.view?.interactionMode ?? 'unknown'} | Questions and plans, no implementation.` : 'Worker manages the working branch.'} y={0.86} size={0.032} />
            {test.state.enabled && !composing ? <>
                <Button text={`Voice Test: ${test.state.selected ? 'ON' : 'OFF'}`} x={0} y={-1.27} width={1.3}
                    onClick={() => {
                        const selected = !test.state.selected;
                        voice.controller.cancel(); speech.controller.stop(); test.controller.capturedTest(selected);
                        if (selected) spaceCanvasXrBindings.notify('Voice Test: saves audio + transcripts for 24h; nothing sent.');
                    }} />
                {test.state.selected ? <Label text={`${test.state.phase}${test.state.comparing ? ' | Whisper pending' : test.state.whisper ? ' | Whisper ready' : ''}${test.state.error ? ` | ${test.state.error}` : ''}`}
                    y={0.72} size={0.028} /> : null}
                {test.state.selected && test.state.phase === 'complete' && !test.state.comparing && test.state.error && !test.state.whisper ?
                    <Button text="Retry Whisper" x={0.85} y={-0.16} width={1.1} onClick={() => void test.controller.compare()} /> : null}
            </> : null}
            {!composing ? <>
                <Label text={pages[page]!} y={0.51} />
                <Button text="<" x={-1.1} y={0.17} disabled={page === 0} onClick={() => setPage(page - 1)} />
                <Label text={`Messages ${page + 1}/${pages.length}`} y={0.17} size={0.032} />
                <Button text=">" x={1.1} y={0.17} disabled={page >= pages.length - 1} onClick={() => setPage(page + 1)} />
                {error ? <Label text={errorPages[errorPage]!} y={0.01} size={0.029} /> : null}
                {error ? <Button text="More error" x={-1} y={-0.16} width={0.65}
                    disabled={errorPages.length < 2} onClick={() => setErrorPage((errorPage + 1) % errorPages.length)} /> : null}
                {state.error ? <Button text="Retry" x={1.1} y={-0.16} width={0.5}
                    disabled={state.loading || state.sending} onClick={controller.retry} /> : null}
            </> : <Label text="Type with your Bluetooth keyboard. Esc ends typing; Ctrl+Enter or Send submits." y={0.72} size={0.031} />}
            {composing || state.draft ? <>
            <Label text={draftPages[draftPage]!} y={composing ? 0.51 : -0.32} size={0.038} />
            <Button text="< draft" x={-1.05} y={composing ? 0.3 : -0.49} width={0.55} disabled={draftPage === 0} onClick={() => setDraftPage(draftPage - 1)} />
            <Label text={`${state.draft.length}/${MAX_AGENT_INSTRUCTION_LENGTH} - draft ${draftPage + 1}/${draftPages.length}`} y={composing ? 0.3 : -0.49} size={0.027} />
            <Button text="draft >" x={1.05} y={composing ? 0.3 : -0.49} width={0.55} disabled={draftPage >= draftPages.length - 1} onClick={() => setDraftPage(draftPage + 1)} />
            </> : null}
            <Button text={composing ? 'Done typing' : 'Compose'} x={-0.8} y={composing ? 0.1 : -0.65} width={0.8} disabled={locked}
                onClick={() => setComposing(!composing)} />
            {composing || state.draft ? <Button text="Send" x={0.8} y={composing ? 0.1 : -0.65} width={0.7} disabled={locked || !state.draft.trim()}
                onClick={() => { setComposing(false); void controller.send(); }} /> : null}
            {composing ? null : <>
                <Button text={voiceTools ? 'Hide voice tools' : 'Voice tools'} x={0} y={-0.87} width={1.2}
                    onClick={() => setVoiceTools(!voiceTools)} />
                {voiceTools ? <>
                    <Button text="Spoken replies" x={-0.7} y={-1.12} width={1.2} onClick={() => setReading(true)} />
                    {voice.state.enabled ? <Button text="Voice draft" x={0.7} y={-1.12} width={1.1} disabled={!!locked && !state.voiceBusy}
                        onClick={() => { microphone.controller.dispose(); setComposing(false); setVoicing(true); }} /> : null}
                </> : null}
            </>}
            </>}
        </group>}
    </group>;
}

function XrAgentCaption({ text, status, streaming, distance }: { text: string; status: string; streaming: boolean; distance: number }) {
    const pages = chatTextPages(text, 64, 5);
    const [page, setPage] = useState(0);
    useEffect(() => {
        if (streaming || page >= pages.length - 1) return;
        const timer = setTimeout(() => setPage(current => current + 1), 8000);
        return () => clearTimeout(timer);
    }, [streaming, page, pages.length]);
    const position = streaming ? pages.length - 1 : Math.min(page, pages.length - 1);
    return <group position={[0, -0.75 * distance / 2.2, -distance]} scale={distance / 2.2}>
        <mesh raycast={() => null} renderOrder={11000}>
            <planeGeometry args={[3.25, 0.95]} />
            <meshBasicMaterial color="#102030" transparent opacity={0.9} depthTest={false} depthWrite={false} />
        </mesh>
        <Label text={pages[position]!} y={0.12} size={0.033} width={3.1} />
        {status ? <Label text={status} y={-0.22} size={0.026} width={3.1} /> : null}
        <Label text={`Agent captions ${position + 1}/${pages.length} | Y: full conversation`} y={-0.4} size={0.022} width={3.1} />
    </group>;
}

function XrSpeechPlayback() {
    const chat = useAgentChat();
    const { state, controller } = useSpeechPlayback();
    const microphone = useMicrophoneTest();
    const waiting = chat.state.view?.approvals?.length ? 'No completed answer yet: command approval required. Open Sessions / requests to inspect it.'
        : chat.state.view?.inputs?.length ? 'No completed answer yet: the Agent needs your input in Sessions / requests.'
            : chat.state.view?.plans?.some(plan => plan.pending) ? 'A plan is waiting for review in Sessions / requests.'
                : chat.state.view?.activeTurnId ? 'The Agent has not finished its reply yet.' : undefined;
    const text = state.error ?? waiting ?? (state.phase === 'speaking' ? 'Reading reply aloud...'
        : `${state.backend === 'forge' ? 'Private Forge English voice' : 'Local browser voice'}. Code and links are skipped.`);
    const pages = chatTextPages(text, 48, 5);
    const [position, setPosition] = useState(0);
    const page = Math.min(position, pages.length - 1);
    const disabled = !state.supported || !!chat.state.voiceBusy || microphone.state.phase !== 'idle';
    return <>
        <Label text="Spoken agent replies" y={0.9} />
        <Label text={pages[page]!} y={0.52} size={0.04} />
        <Button text="<" x={-0.9} y={0.12} disabled={page === 0} onClick={() => setPosition(page - 1)} />
        <Label text={`${page + 1}/${pages.length}`} y={0.12} size={0.03} />
        <Button text=">" x={0.9} y={0.12} disabled={page >= pages.length - 1} onClick={() => setPosition(page + 1)} />
        <Button text="Read aloud" x={0} y={-0.16} width={1.4}
            disabled={disabled || chat.state.loading || chat.state.sending || chat.state.reconnecting
                || chat.state.view?.status !== 'idle' || !chat.state.view.entries.some(entry => entry.role === 'agent')}
            onClick={controller.readAloud} />
        <Button text="Stop speaking" x={0} y={-0.42} width={1.4}
            disabled={state.phase !== 'speaking'} onClick={controller.stop} />
        <Button text={`Auto-speak: ${state.autoSpeak ? 'on' : 'off'}`} x={0} y={-0.7} width={1.6}
            disabled={!state.supported} onClick={() => controller.setAutoSpeak(!state.autoSpeak)} />
        <Label text="Auto-speak starts on. New completed replies only; microphone use stops speech. Playback continues when this panel closes." y={-1.02} size={0.035} />
    </>;
}

function XrVoiceInput() {
    const chat = useAgentChat();
    const { state, controller } = useVoiceInput();
    const [position, setPosition] = useState(0);
    const pages = chatTextPages(voiceInputSummary(state), 48, 5);
    const page = Math.min(position, pages.length - 1);
    return <>
        <Label text="Voice -> draft -> review -> Send" y={0.9} />
        <Label text={pages[page]!} y={0.52} size={0.04} />
        <Button text="<" x={-0.9} y={0.12} disabled={page === 0} onClick={() => setPosition(page - 1)} />
        <Label text={`${page + 1}/${pages.length}`} y={0.12} size={0.03} />
        <Button text=">" x={0.9} y={0.12} disabled={page >= pages.length - 1} onClick={() => setPosition(page + 1)} />
        <Button text="Record" x={-0.7} y={-0.16} width={1} disabled={!!chatLocked(chat.state) || state.phase !== 'idle'}
            onClick={() => { void controller.record(); }} />
        <Button text="Stop" x={0.7} y={-0.16} width={1} disabled={state.phase !== 'recording'} onClick={controller.stop} />
        <Button text="Transcribe / retry" x={0} y={-0.42} width={1.7}
            disabled={!controller.hasRecording() || !['ready', 'error'].includes(state.phase)}
            onClick={() => { void controller.transcribe(); }} />
        <Button text="Cancel voice" x={0} y={-0.7} width={1.2} disabled={state.phase === 'idle'} onClick={controller.cancel} />
        <Label text="After transcription, Back to chat to review/edit the draft. Nothing is sent automatically." y={-1.02} size={0.035} />
    </>;
}

function XrAhpControls() {
    const stream = useAhpStreamDiagnostics();
    const { state, controller } = useAgentChat();
    const [tab, setTab] = useState<'sessions' | 'requests' | 'files' | 'diagnostics'>('requests');
    const [index, setIndex] = useState(0);
    const [page, setPage] = useState(0);
    const [optionIndex, setOptionIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [typing, setTyping] = useState(false);
    const disabled = !!(state.loading || state.sending || state.reconnecting);
    const approvals = state.view?.approvals ?? [];
    const inputs = state.view?.inputs ?? [];
    const plans = state.view?.plans?.filter(plan => plan.pending) ?? [];
    const changes = selectedAgentChanges(state);
    const planned = selectedPlannedFiles(state);
    const showingPlanned = state.changesetId === 'planned';
    const changesets = state.view?.changesets ?? [];
    const filesSelected = tab === 'files';
    const count = tab === 'sessions' ? state.sessions?.length ?? 0 : plans.length + approvals.length + inputs.length;
    const position = Math.min(index, Math.max(0, count - 1));
    const selectedSession = state.sessions?.[position];
    const plan = tab === 'requests' ? plans[position] : undefined;
    const approval = tab === 'requests' && position >= plans.length ? approvals[position - plans.length] : undefined;
    const request = tab === 'requests' && position >= plans.length + approvals.length ? inputs[position - plans.length - approvals.length] : undefined;
    const questions = request?.questions ?? [];
    const question = questions[Math.min(questionIndex, Math.max(0, questions.length - 1))];
    const draft = request && question ? state.inputValues?.[request.id]?.[question.id] ?? '' : '';
    const edit = (key: string) => {
        if (!disabled && request && question) {
            const current = controller.getSnapshot().inputValues?.[request.id]?.[question.id] ?? '';
            controller.setInputValue(request.id, question.id, editChatDraft(current, key));
        }
    };
    useEffect(() => {
        if (!typing || !request || !question) return;
        const keydown = (event: KeyboardEvent) => {
            if (event.isComposing) return;
            event.stopImmediatePropagation();
            if (event.key === 'Escape') { event.preventDefault(); setTyping(false); return; }
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            event.preventDefault();
            if (event.key === 'Enter' || event.key === 'Backspace' || Array.from(event.key).length === 1) edit(event.key);
        };
        const paste = (event: ClipboardEvent) => {
            event.preventDefault(); event.stopImmediatePropagation();
            if (event.clipboardData) edit(event.clipboardData.getData('text/plain'));
        };
        window.addEventListener('keydown', keydown, true);
        window.addEventListener('paste', paste, true);
        return () => { window.removeEventListener('keydown', keydown, true); window.removeEventListener('paste', paste, true); };
    });
    const detail = tab === 'diagnostics' ? `Stream: ${stream.phase}\nLast stream event: ${stream.updatedAt || 'none'}\n${ahpDiagnosticsText(state)}`
        : filesSelected ? state.loading || state.reconnecting ? 'Reconnecting; file highlights are suspended.'
        : showingPlanned ? [plannedFilesSummary(planned), ...planned?.files.map(file => `${file.intent}: ${file.path}`) ?? []].join('\n\n')
        : changes ? [changesetSummary(changes), ...changes.files.map(changeFileText)].join('\n\n')
            : state.changesetId === 'none' ? 'File highlights are off.' : 'The host has not advertised a supported version of this changeset.'
        : tab === 'sessions' ? `${state.development ? `LIVE: ${state.development.id}\nBranch: ${state.development.branch}\nShared worktree. Other clients can edit it and end XR via page reload.\n` : ''}${selectedSession?.title ?? 'No sessions. Refresh the list or start a new chat.'}\n${selectedSession?.threadId ?? ''}\nChat mode: ${state.view?.interactionMode ?? 'unknown'}. Questions and plans only in Cosmos; other clients can change modes. Not a filesystem sandbox.\n${state.view?.permissionSummary ?? ''}`
        : plan ? `${plan.title}\n${plan.content}\nFinish planning to ask a follow-up. The plan stays in history; it is not approved or implemented. Scheduling is not available.`
        : approval ? `${approval.title}\n${approval.detail}`
            : request ? [request.message, request.url ? `Review URL request in VS Code: ${request.url}` : '',
                question ? `${question.title ?? ''}\n${question.message}\nType: ${question.kind}${question.required ? ' (required)' : ''}` : '',
                question && 'options' in question ? question.options.map(option => `${option.id}: ${option.label}`).join('\n') : '',
                question?.kind === 'multi-select' ? 'Enter a JSON array of option IDs, e.g. ["first","second"].' : '',
                question?.kind === 'boolean' ? 'Enter true or false.' : '',
            ].filter(Boolean).join('\n') : 'No pending requests.';
    const detailPages = chatTextPages([detail, state.error].filter(Boolean).join('\n'), 55, typing ? 3 : 8);
    const currentPage = Math.min(page, detailPages.length - 1);
    const options = approval?.options?.length ? approval.options : [
        { id: '', label: 'Approve once', kind: 'approve' }, { id: '', label: 'Deny', kind: 'deny' },
    ];
    const option = options[optionIndex % options.length]!;
    const navigate = (next: number) => { setIndex(next); setPage(0); setOptionIndex(0); setQuestionIndex(0); setTyping(false); };
    return <>
        <Label text="Forge sessions, requests and file changes" y={0.96} size={0.04} />
        {!typing ? <>
            <Button text="Sessions" x={-1.06} y={0.79} width={0.6} onClick={() => { setTab('sessions'); navigate(0); }} />
            <Button text="Requests" x={-0.4} y={0.79} width={0.6} onClick={() => { setTab('requests'); navigate(0); }} />
            <Button text="Files" x={0.19} y={0.79} width={0.48} onClick={() => { setTab('files'); navigate(0); }} />
            <Button text="Advanced" x={0.85} y={0.79} width={0.75}
                onClick={() => { setTab('diagnostics'); navigate(0); }} />
            {state.error ? <Button text="Retry connection" x={state.view?.activeTurnId ? -0.8 : 0} y={-1.05} width={1.2}
                disabled={state.loading || state.sending} onClick={controller.retry} /> : null}
            <Label text={detailPages[currentPage]!} y={0.43} size={0.033} />
            <Button text="< text" x={-1.05} y={0.07} width={0.55} disabled={!currentPage} onClick={() => setPage(currentPage - 1)} />
            <Label text={`${currentPage + 1}/${detailPages.length}`} y={0.07} size={0.03} />
            <Button text="text >" x={1.05} y={0.07} width={0.55} disabled={currentPage === detailPages.length - 1} onClick={() => setPage(currentPage + 1)} />
            {!filesSelected && tab !== 'diagnostics' ? <>
                <Button text="< item" x={-1.05} y={-0.12} width={0.55} disabled={!position} onClick={() => navigate(position - 1)} />
                <Label text={`${count ? position + 1 : 0}/${count}`} y={-0.12} size={0.03} />
                <Button text="item >" x={1.05} y={-0.12} width={0.55} disabled={position >= count - 1} onClick={() => navigate(position + 1)} />
            </> : null}
            {filesSelected ? <>
                <Button text="Next changeset" x={0} y={-0.35} width={1.6} disabled={!changesets.length || disabled}
                    onClick={() => {
                        const next = changesets[(changesets.findIndex(c => c.id === (state.changesetId ?? 'session')) + 1) % changesets.length]!;
                        controller.selectChangeset(next.id); setPage(0);
                    }} />
                <Button text="Planned files" x={0} y={-0.58} width={1.6} disabled={disabled}
                    onClick={() => { controller.selectChangeset('planned'); setPage(0); }} />
                <Button text="Highlights off" x={0} y={-0.8} width={1.6} onClick={() => controller.selectChangeset('none')} />
            </> : tab === 'sessions' ? <>
                <Button text="Open session" x={-0.65} y={-0.4} width={1.1} disabled={state.loading || state.sending || !selectedSession}
                    onClick={() => selectedSession && controller.selectSession(selectedSession.threadId)} />
                <Button text="Refresh list" x={0.65} y={-0.4} width={1.1} onClick={() => void controller.refreshSessions()} />
            </> : plan ? <Button text="Finish planning" x={0} y={-0.5} width={1.6} disabled={disabled}
                onClick={() => void controller.command({ kind: 'input', requestId: plan.requestId, response: 'cancel', values: {} })} />
            : approval ? <>
                <Button text="< option" x={-1} y={-0.35} width={0.6} onClick={() => setOptionIndex((optionIndex + options.length - 1) % options.length)} />
                <Label text={option.label} y={-0.35} size={0.032} width={1.2} />
                <Button text="option >" x={1} y={-0.35} width={0.6} onClick={() => setOptionIndex((optionIndex + 1) % options.length)} />
                <Button text={`Confirm: ${option.label}`} x={0} y={-0.58} width={2.2}
                    disabled={disabled || (option.kind === 'approve' && !approval.canApprove)}
                    onClick={() => void controller.command({ kind: 'approval', turnId: approval.turnId,
                        toolCallId: approval.toolCallId, approved: option.kind === 'approve', ...(option.id ? { optionId: option.id } : {}) })} />
            </> : request ? <>
                <Label text={`Answer: ${draft || '(empty)'}`} y={-0.31} size={0.03} />
                <Button text="Next question" x={-0.7} y={-0.5} width={1.1} disabled={questions.length < 2}
                    onClick={() => { setQuestionIndex((questionIndex + 1) % questions.length); setPage(0); }} />
                <Button text="Type answer" x={0.7} y={-0.5} width={1.1} disabled={disabled || !question || !!request.url} onClick={() => setTyping(true)} />
                <Button text="Submit" x={-0.85} y={-0.7} width={0.7} disabled={disabled || !!request.url} onClick={() => void controller.command({
                    kind: 'input', requestId: request.id, response: 'accept', values: state.inputValues?.[request.id] ?? {},
                })} />
                <Button text="Decline" x={0} y={-0.7} width={0.7} disabled={disabled} onClick={() => void controller.command({
                    kind: 'input', requestId: request.id, response: 'decline', values: {},
                })} />
                <Button text="Cancel" x={0.85} y={-0.7} width={0.7} disabled={disabled} onClick={() => void controller.command({
                    kind: 'input', requestId: request.id, response: 'cancel', values: {},
                })} />
            </> : null}
            {state.view?.activeTurnId ? <Button text="Cancel active turn" x={0.8} y={-1.05} width={1.1} disabled={disabled}
                onClick={() => void controller.command({ kind: 'cancel', turnId: state.view!.activeTurnId! })} /> : null}
        </> : <>
            <Label text={detailPages[currentPage]!} y={0.73} size={0.03} />
            <Label text={chatTextPages(draft || '(empty)', 52, 3).at(-1)!} y={0.48} size={0.034} />
            <Label text="Type with your Bluetooth keyboard. Escape to finish." y={0.2} size={0.032} />
            <Button text="Done typing" x={0} y={-0.1} width={1} onClick={() => setTyping(false)} />
        </>}
    </>;
}
